import { useCallback, useEffect, useRef, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FileUp, FileText, Image as ImageIcon, Trash2, Loader2, Search, Download, BookMarked,
} from "lucide-react";
import { toast } from "sonner";

const FILE_TYPE_LABEL = {
  manuale: "Manuale",
  esploso: "Esploso",
  schema: "Schema",
  altro: "Altro",
};

const FILE_TYPE_COLORS = {
  manuale: "bg-blue-50 text-blue-800 border-blue-200",
  esploso: "bg-purple-50 text-purple-800 border-purple-200",
  schema: "bg-amber-50 text-amber-800 border-amber-200",
  altro: "bg-zinc-50 text-zinc-700 border-zinc-200",
};

function FileCard({ file, isAdmin, onDelete, compact }) {
  const isImage = (file.content_type || "").startsWith("image/");
  const url = `${API_BASE}/knowledge/files/${file.id}/download`;
  return (
    <Card
      className={`rounded-sm border-zinc-200 bg-white ${compact ? "p-3" : "p-4"} flex ${compact ? "gap-3" : "gap-4"}`}
      data-testid={`kb-file-${file.id}`}
    >
      <div className={`${compact ? "h-14 w-14" : "h-20 w-20"} shrink-0 rounded-sm bg-zinc-100 flex items-center justify-center overflow-hidden`}>
        {isImage ? (
          <img src={url} alt={file.title} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <FileText className={compact ? "h-6 w-6 text-brand" : "h-8 w-8 text-brand"} strokeWidth={1.5} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className={`${compact ? "text-sm" : "text-base"} font-medium text-zinc-900 hover:text-brand line-clamp-2 leading-tight`}
          data-testid={`kb-file-open-${file.id}`}
        >
          {file.title || file.original_filename}
        </a>
        <div className="flex items-center flex-wrap gap-1 mt-2">
          <Badge className={`rounded-sm text-[9px] uppercase tracking-widest border ${FILE_TYPE_COLORS[file.file_type] || FILE_TYPE_COLORS.altro}`}>
            {FILE_TYPE_LABEL[file.file_type] || file.file_type}
          </Badge>
          {file.model && (
            <Badge className="rounded-sm text-[9px] uppercase tracking-widest border bg-brand-muted text-brand border-brand/20">
              {file.model}
            </Badge>
          )}
          {isImage ? (
            <Badge className="rounded-sm text-[9px] border bg-zinc-50 text-zinc-500 border-zinc-200 uppercase">IMG</Badge>
          ) : (
            <Badge className="rounded-sm text-[9px] border bg-zinc-50 text-zinc-500 border-zinc-200 uppercase">PDF</Badge>
          )}
        </div>
        {!compact && file.description && (
          <div className="text-xs text-zinc-500 mt-2 line-clamp-2">{file.description}</div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          data-testid={`kb-file-download-${file.id}`}
          className="h-8 w-8 rounded-sm border border-zinc-300 text-zinc-600 hover:bg-zinc-50 flex items-center justify-center"
          title="Scarica / Apri"
        >
          <Download className="h-3.5 w-3.5" />
        </a>
        {isAdmin && (
          <button
            onClick={() => onDelete(file)}
            data-testid={`kb-file-delete-${file.id}`}
            className="h-8 w-8 rounded-sm border border-zinc-300 text-zinc-400 hover:text-red-600 hover:border-red-200 flex items-center justify-center"
            title="Elimina"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </Card>
  );
}

function UploadDialog({ open, onOpenChange, defaultModel, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [model, setModel] = useState(defaultModel || "");
  const [fileType, setFileType] = useState("manuale");

  useEffect(() => {
    if (open) {
      setTitle("");
      setDescription("");
      setModel(defaultModel || "");
      setFileType("manuale");
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open, defaultModel]);

  const submit = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Seleziona un file"); return; }
    const fd = new FormData();
    fd.append("file", f);
    if (title.trim()) fd.append("title", title.trim());
    if (description.trim()) fd.append("description", description.trim());
    if (model.trim()) fd.append("model", model.trim());
    fd.append("file_type", fileType);
    setUploading(true);
    try {
      const { data } = await api.post("/knowledge/files", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`File caricato: ${data.title}${data.has_text ? " (testo estratto per AI)" : ""}`);
      onOpenChange(false);
      onUploaded?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm" data-testid="kb-file-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Carica manuale / esploso</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">File (PDF, JPG, PNG, WebP · max 25MB)</Label>
            <Input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,application/pdf,image/*"
              data-testid="kb-file-input"
              className="mt-1 rounded-sm"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Titolo (opzionale)</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="es. Manuale QUATTRO+4 rev.3"
              data-testid="kb-file-title"
              className="mt-1 rounded-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Tipo</Label>
              <Select value={fileType} onValueChange={setFileType}>
                <SelectTrigger data-testid="kb-file-type" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manuale">Manuale</SelectItem>
                  <SelectItem value="esploso">Esploso</SelectItem>
                  <SelectItem value="schema">Schema</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Modello dispenser (opzionale)</Label>
              <Input
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="es. QUATTRO+4 110V USA SERIE 3 PLUS MAGNETIC"
                data-testid="kb-file-model"
                className="mt-1 rounded-sm"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Descrizione (opzionale)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Breve descrizione del contenuto"
              rows={2}
              data-testid="kb-file-description"
              className="mt-1 rounded-sm"
            />
          </div>
          <div className="text-[11px] text-zinc-500 leading-relaxed">
            <strong>PDF</strong>: il testo viene estratto automaticamente e usato dall&apos;AI per suggerire soluzioni.
            <br />
            <strong>Immagini</strong>: consultabili come biblioteca visiva (non usate dall&apos;AI).
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button
            data-testid="kb-file-upload-submit"
            onClick={submit}
            disabled={uploading}
            className="bg-brand hover:bg-brand-dark text-white rounded-sm"
          >
            {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Caricamento...</> : <><FileUp className="h-4 w-4 mr-2" /> Carica</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * KnowledgeFilesPanel
 *
 * Full section for /conoscenza that lists uploaded manuals/esplosi with upload (admin only).
 * Also usable in ticket detail sidebar via `compact` + `modelFilter` props.
 */
export function KnowledgeFilesPanel({ isAdmin, modelFilter, compact = false, limit = 100 }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (modelFilter) params.model = modelFilter;
      if (typeFilter !== "all") params.file_type = typeFilter;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/knowledge/files", { params });
      setFiles(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [modelFilter, typeFilter, q, limit]);

  useEffect(() => { load(); }, [load]);

  const del = async (file) => {
    if (!window.confirm(`Eliminare "${file.title || file.original_filename}"?`)) return;
    try {
      await api.delete(`/knowledge/files/${file.id}`);
      toast.success("File eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // COMPACT VIEW (for TicketDetail sidebar)
  if (compact) {
    if (loading) {
      return (
        <div className="flex items-center gap-2 text-xs text-zinc-500 py-3">
          <Loader2 className="h-3 w-3 animate-spin" /> Caricamento manuali...
        </div>
      );
    }
    if (files.length === 0) {
      return (
        <div className="text-xs text-zinc-500 py-2" data-testid="kb-files-compact-empty">
          Nessun manuale disponibile per questo modello.
        </div>
      );
    }
    return (
      <div className="space-y-2" data-testid="kb-files-compact">
        {files.map((f) => (
          <FileCard key={f.id} file={f} isAdmin={isAdmin} onDelete={del} compact />
        ))}
      </div>
    );
  }

  // FULL VIEW (for /conoscenza page)
  return (
    <Card className="rounded-sm border-zinc-200 bg-white p-5 sm:p-6" data-testid="kb-files-panel">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BookMarked className="h-4 w-4 text-brand" strokeWidth={1.75} />
            <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-950">Manuali & Esplosi</h3>
          </div>
          <p className="text-xs text-zinc-500 max-w-md">
            Biblioteca tecnica: PDF (testo estratto, usato dall&apos;AI) e immagini (esplosi/schemi consultabili dai tecnici).
          </p>
        </div>
        {isAdmin && (
          <Button
            data-testid="kb-files-upload-btn"
            onClick={() => setUploadOpen(true)}
            className="bg-brand hover:bg-brand-dark text-white rounded-sm w-full sm:w-auto"
          >
            <FileUp className="h-4 w-4 mr-2" /> Carica file
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
          <Input
            data-testid="kb-files-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per titolo, modello, descrizione..."
            className="pl-9 rounded-sm h-9 text-sm"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="kb-files-type-filter" className="flex-1 min-w-[140px] sm:flex-none sm:w-44 h-9 rounded-sm text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            <SelectItem value="manuale">Manuali</SelectItem>
            <SelectItem value="esploso">Esplosi</SelectItem>
            <SelectItem value="schema">Schemi</SelectItem>
            <SelectItem value="altro">Altro</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10 text-sm text-zinc-500">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Caricamento...
        </div>
      )}
      {!loading && files.length === 0 && (
        <div className="text-center py-10">
          <ImageIcon className="h-8 w-8 mx-auto mb-2 text-zinc-300" strokeWidth={1.25} />
          <div className="text-sm text-zinc-500">Nessun file caricato.</div>
          {isAdmin && (
            <div className="text-xs text-zinc-400 mt-1">Clicca &quot;Carica file&quot; per aggiungere manuali o esplosi.</div>
          )}
        </div>
      )}
      {!loading && files.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {files.map((f) => (
            <FileCard key={f.id} file={f} isAdmin={isAdmin} onDelete={del} />
          ))}
        </div>
      )}

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        defaultModel={modelFilter}
        onUploaded={load}
      />
    </Card>
  );
}
